'use client';

import { useEffect, useRef, useState } from 'react';

type FractalType = 'mandelbrot' | 'julia' | 'sierpinski' | 'koch';

const FRACTALS: { value: FractalType; label: string }[] = [
  { value: 'mandelbrot', label: 'Mandelbrot set' },
  { value: 'julia', label: 'Julia set' },
  { value: 'sierpinski', label: 'Sierpinski triangle' },
  { value: 'koch', label: 'Koch snowflake' },
];

const WIDTH = 640;
const HEIGHT = 640;
const MAX_ITERATIONS = 500;
const MAX_DEPTH = 8;

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

function drawEscape(
  ctx: CanvasRenderingContext2D,
  type: 'mandelbrot' | 'julia',
  iterations: number,
  zoom: number,
  baseHue: number,
) {
  const image = ctx.createImageData(WIDTH, HEIGHT);
  const data = image.data;
  const scale = 1 / zoom;
  const centerX = type === 'mandelbrot' ? -0.6 : 0;
  const cRe = -0.7;
  const cIm = 0.27015;

  for (let py = 0; py < HEIGHT; py += 1) {
    for (let px = 0; px < WIDTH; px += 1) {
      let zx = ((px - WIDTH / 2) / (WIDTH / 4)) * scale + centerX;
      let zy = ((py - HEIGHT / 2) / (HEIGHT / 4)) * scale;
      let x0 = zx;
      let y0 = zy;
      if (type === 'julia') {
        x0 = cRe;
        y0 = cIm;
      }
      let iteration = 0;
      while (zx * zx + zy * zy <= 4 && iteration < iterations) {
        const xTemp = zx * zx - zy * zy + x0;
        zy = 2 * zx * zy + y0;
        zx = xTemp;
        iteration += 1;
      }
      const offset = (py * WIDTH + px) * 4;
      if (iteration >= iterations) {
        data[offset] = 8;
        data[offset + 1] = 10;
        data[offset + 2] = 20;
      } else {
        const hue = (baseHue + (iteration / iterations) * 300) % 360;
        const [r, g, b] = hslToRgb(hue, 0.75, 0.55);
        data[offset] = r;
        data[offset + 1] = g;
        data[offset + 2] = b;
      }
      data[offset + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
}

function drawSierpinski(ctx: CanvasRenderingContext2D, depth: number, color: string) {
  ctx.fillStyle = '#0a0f1e';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.fillStyle = color;
  const margin = 40;
  const top: [number, number] = [WIDTH / 2, margin];
  const left: [number, number] = [margin, HEIGHT - margin];
  const right: [number, number] = [WIDTH - margin, HEIGHT - margin];

  const mid = (a: [number, number], b: [number, number]): [number, number] => [
    (a[0] + b[0]) / 2,
    (a[1] + b[1]) / 2,
  ];

  const triangle = (a: [number, number], b: [number, number], c: [number, number]) => {
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
    ctx.lineTo(c[0], c[1]);
    ctx.closePath();
    ctx.fill();
  };

  const recurse = (
    a: [number, number],
    b: [number, number],
    c: [number, number],
    level: number,
  ) => {
    if (level === 0) {
      triangle(a, b, c);
      return;
    }
    recurse(a, mid(a, b), mid(a, c), level - 1);
    recurse(mid(a, b), b, mid(b, c), level - 1);
    recurse(mid(a, c), mid(b, c), c, level - 1);
  };

  recurse(top, left, right, depth);
}

function drawKoch(ctx: CanvasRenderingContext2D, depth: number, color: string) {
  ctx.fillStyle = '#0a0f1e';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.4;
  ctx.lineJoin = 'round';

  const size = 460;
  const height = (size * Math.sqrt(3)) / 2;
  const cx = WIDTH / 2;
  const cy = HEIGHT / 2 + height / 6;
  const p1: [number, number] = [cx - size / 2, cy - height / 3];
  const p2: [number, number] = [cx + size / 2, cy - height / 3];
  const p3: [number, number] = [cx, cy + (2 * height) / 3];

  const segment = (a: [number, number], b: [number, number], level: number) => {
    if (level === 0) {
      ctx.lineTo(b[0], b[1]);
      return;
    }
    const dx = (b[0] - a[0]) / 3;
    const dy = (b[1] - a[1]) / 3;
    const pA: [number, number] = [a[0] + dx, a[1] + dy];
    const pB: [number, number] = [a[0] + 2 * dx, a[1] + 2 * dy];
    const angle = Math.atan2(b[1] - a[1], b[0] - a[0]) - Math.PI / 3;
    const dist = Math.hypot(dx, dy);
    const peak: [number, number] = [pA[0] + Math.cos(angle) * dist, pA[1] + Math.sin(angle) * dist];
    segment(a, pA, level - 1);
    segment(pA, peak, level - 1);
    segment(peak, pB, level - 1);
    segment(pB, b, level - 1);
  };

  ctx.beginPath();
  ctx.moveTo(p1[0], p1[1]);
  segment(p1, p2, depth);
  segment(p2, p3, depth);
  segment(p3, p1, depth);
  ctx.stroke();
}

export default function BrowserFractalGenerator() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [type, setType] = useState<FractalType>('mandelbrot');
  const [iterations, setIterations] = useState(120);
  const [zoom, setZoom] = useState(1);
  const [depth, setDepth] = useState(5);
  const [color, setColor] = useState('#38bdf8');
  const [hue, setHue] = useState(200);

  const isEscape = type === 'mandelbrot' || type === 'julia';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (type === 'mandelbrot' || type === 'julia') {
      drawEscape(ctx, type, iterations, zoom, hue);
    } else if (type === 'sierpinski') {
      drawSierpinski(ctx, depth, color);
    } else {
      drawKoch(ctx, depth, color);
    }
  }, [type, iterations, zoom, depth, color, hue]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}-fractal.png`;
    document.body.append(link);
    link.click();
    link.remove();
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Fractal art</span>
        <h2 className="tool-live-title">Fractal generator</h2>
        <p className="tool-live-subtitle">
          Render a Mandelbrot set, Julia set, Sierpinski triangle, or Koch snowflake on a canvas,
          tweak the detail and color, then download a PNG. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Fractal type</span>
              <select onChange={(event) => setType(event.target.value as FractalType)} value={type}>
                {FRACTALS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            {isEscape ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Iterations: {iterations}</span>
                  <input
                    max={MAX_ITERATIONS}
                    min={20}
                    onChange={(event) => setIterations(Number(event.target.value))}
                    step={10}
                    type="range"
                    value={iterations}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Zoom: {zoom.toFixed(1)}x</span>
                  <input
                    max={40}
                    min={0.5}
                    onChange={(event) => setZoom(Number(event.target.value))}
                    step={0.5}
                    type="range"
                    value={zoom}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Base hue: {hue}°</span>
                  <input
                    max={360}
                    min={0}
                    onChange={(event) => setHue(Number(event.target.value))}
                    step={1}
                    type="range"
                    value={hue}
                  />
                </label>
              </>
            ) : (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Recursion depth: {depth}</span>
                  <input
                    max={MAX_DEPTH}
                    min={0}
                    onChange={(event) => setDepth(Number(event.target.value))}
                    step={1}
                    type="range"
                    value={depth}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Color</span>
                  <input
                    onChange={(event) => setColor(event.target.value)}
                    type="color"
                    value={color}
                  />
                </label>
              </>
            )}
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleDownload} type="button">
              Download PNG
            </button>
          </div>
          <p className="tool-live-panel-note">
            Iterations and recursion depth are capped so rendering stays fast. Deeper Koch and
            Sierpinski levels add exponentially more detail.
          </p>
        </section>

        <section className="tool-live-preview">
          <canvas
            height={HEIGHT}
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: 'block',
              background: '#0a0f1e',
            }}
            width={WIDTH}
          />
          <p className="tool-live-panel-note">
            The image is drawn locally with an HTML canvas. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
